/**
 * The state rule behind `useFormError` — kept out of FormError.tsx so
 * it can be tested without a DOM (the component imports a CSS module,
 * and this app's test runner is plain `node --test` via tsx).
 *
 * THE RULE: an error is an OCCURRENCE, not a string.
 *
 * Submit handlers in this app clear then re-set inside one tick:
 *
 *     setError('');                     // top of handleSubmit
 *     ...                               // no await
 *     setError('Set a guest price…');   // a guard
 *
 * React batches those, so with plain string state it sees 'X' -> 'X',
 * bails out of rendering, and the second identical rejection is
 * completely invisible — no scroll, no re-announce. Stamping a
 * monotonic nonce makes the two states genuinely different, so the
 * bail-out cannot happen and every occurrence lands.
 */

export interface FormErrorSignal {
  message: string;
  nonce: number;
}

/**
 * A stateful factory: each call with a non-empty message returns a
 * NEW signal with a strictly higher nonce. Empty/null clears (null).
 *
 * One sequencer per form. `useFormError` holds it in a ref so bumping
 * never itself schedules a render and it survives a batch.
 */
export function createFormErrorSequencer(): (
  message: string | null | undefined,
) => FormErrorSignal | null {
  let seq = 0;
  return (message) => {
    if (!message) return null;
    seq += 1;
    return { message, nonce: seq };
  };
}
